import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getDocumentStatusOptions,
  getDocumentTypeFilterOptions,
  translateDocumentFullName,
} from "src/utils/documentsI18n";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { formatRut } from "../../../../../utils/formatter";
import { parseFilterAmount } from "../../../../../utils/amountInput";
import { documentsDic } from "../../../../../utils/dataDictionary";
import {
  importDocumentsFilter,
  importDocumentsTypes,
  setUpdateDocumentsTypes,
} from "../../../../../redux/actions/documentostributarios";
import { importAlertState } from "../../../../../redux/actions/alert";
import { DownloadFilteredMultiPayoff } from "../../../../../graphql/documents";
import { isDinPath } from "../../../../documentostributarios/dinRowHelpers";
import DownloadFilteredButton from "../../../../shared/DownloadFilteredButton/DownloadFilteredButton";
import { Input, Dropdown, Popup, Icon, Button } from "semantic-ui-react";
import { importEnhancedIcons } from "../../../../../redux/actions/enhancedIcons";
import { eerrMacroCategories } from "../../../../../utils/eerrCategories";
import ObligationsDropdown from "../../../../../containers/ObligationsDropdown/ObligationsDropdown";
import moment from "moment";
import FeatherIcon from "feather-icons-react";
import { Plans } from "src/utils/plans";
import { showAccounting } from "src/utils/accounting";
import SelectorEERR from "src/components/general/js/formTypes/selectorEERR";
import { SelectorCashflow } from "../../formTypes/selectorCashflow/selectorCashflow";
import DateRangeFilter from "../DateRangeFilter/DateRangeFilter";
import "./DocumentFilters.scss";

const CommentFilterIcon = ({ className = "", size = 16, strokeWidth = 1.75 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M9 2C5.13 2 2 4.58 2 7.8C2 9.24 2.6 10.56 3.5 11.6L2.5 15.5L6.5 14.5C7.44 14.83 8.45 15 9.5 15C13.37 15 16.5 12.42 16.5 9.2C16.5 5.98 13.37 2 9 2Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Filtros de área (ESTADO DE RESULTADOS / FLUJO DE CAJA) ocultos por ahora.
// Se conserva el código por si se vuelven a habilitar en el futuro.
const SHOW_AREA_FILTERS = false;

const DocumentFilters = () => {
  const { t, i18n } = useTranslation("documents");
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const filtersContainerRef = useRef(null);

  const [activeFilter, setActiveFilter] = useState(null);
  const [variables, setVariables] = useState({});
  const [searchInputValues, setSearchInputValues] = useState({
    amountStart: '',
    amountEnd: '',
    rut: '',
    name: '',
    docNum: '',
    documentCodes: [],
    obligationTypes: [],
    EERRAreas: [],
    cashflowAreaId: [],
    status: []
  });
  
  const {
    documentsFilter = {},
    documentTypes,
    documentTypesState,
    selectedType,
    excludeDocumentCodes,
  } = useSelector((state) => state.documentostributarios);
  
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { id: companyId, plan } = selectedCompany || {};
  const { roles } = useSelector((state) => state.auth);
  const canShowAccounting = showAccounting(roles, selectedCompany);

  const {
    executeMutation: downloadDocumentsMutation,
    fetching: downloadFetching,
  } = DownloadFilteredMultiPayoff();

  // Descarga en Excel TODOS los documentos que coinciden con los filtros
  // actuales (no solo la página visible), con las mismas columnas que la
  // descarga masiva del rightSidebar. Replica las variables de la tabla
  // (DocumentosTributarios.js) pero sin paginación.
  const handleDownloadExcel = async () => {
    if (downloadFetching) return;

    const pathname = location.pathname;
    const isCreditsModule = pathname === "/cuotas_de_creditos";
    const isDinModule = isDinPath(pathname);
    const creditsCurrencyType = isCreditsModule
      ? searchParams.get("type") || "CLP"
      : null;

    const parsedDocNum = documentsFilter?.docNum
      ? documentsFilter.docNum
          .replaceAll(" ", "")
          .split(";")
          .filter((f) => f.length > 0)
      : undefined;
    const parsedFilters = {
      ...documentsFilter,
      docNum: parsedDocNum?.length ? parsedDocNum : undefined,
    };
    const { documentId, ...cleanFilters } = parsedFilters;

    const getValidFilter = () => {
      if (isCreditsModule) return "ALL";
      if (!selectedType) return "ALL";
      if (pathname === "/documentos_tributarios")
        return ["ALL", "GIVEN", "RECEIVED"].includes(selectedType)
          ? selectedType
          : "ALL";
      if (pathname === "/boletas")
        return ["FEES", "TERCEROS"].includes(selectedType)
          ? selectedType
          : "FEES";
      if (pathname === "/obligaciones") {
        if (selectedType === "ESTIMACION" || selectedType === "INVOICES")
          return "OBLIGATION";
        return ["ALL", "OBLIGATION", "ESTIMACION", "INVOICES"].includes(
          selectedType
        )
          ? selectedType
          : "OBLIGATION";
      }
      if (selectedType === "DIN") return "ALL";
      if (["CLP", "USD", "UF"].includes(selectedType)) return "ALL";
      return selectedType;
    };

    const input = {
      companyId,
      documentTypes: isCreditsModule
        ? ["CREDIT"]
        : isDinModule
          ? ["TAX_DOCUMENT"]
          : documentTypes || ["TAX_DOCUMENT", "OBLIGATION"],
      filter: getValidFilter(),
      ...(isDinModule ? { documentCodes: ["914"] } : {}),
      // Respetar las exclusiones por pestaña que aplica la tabla (p. ej. boletas
      // de honorarios "0" y terceros "1") y forzar siempre "914" (DIN), igual que
      // las variables de DocumentosTributarios.js.
      excludeDocumentCodes:
        isCreditsModule || isDinModule
          ? []
          : [...new Set([...(excludeDocumentCodes || []), "914"])],
      newFilter: isCreditsModule
        ? {
            ...cleanFilters,
            documentType: "CREDIT",
            isHidden: false,
            currencyType: creditsCurrencyType,
          }
        : isDinModule
          ? { ...cleanFilters, isHidden: false }
          : cleanFilters,
    };

    dispatch(
      importAlertState({
        concept: t("filters.download.preparing"),
        content: t("filters.download.preparingHint"),
        state: "loading",
        duration: 10,
      })
    );

    const response = await downloadDocumentsMutation(
      { input },
      { requestPolicy: "network-only" }
    );

    const url = response?.data?.downloadFilteredMultiPayoff;
    if (response?.error || !url) {
      dispatch(
        importAlertState({
          content: t("filters.download.error"),
          state: "error",
          duration: 8,
        })
      );
      return;
    }

    dispatch(
      importAlertState({
        content: t("filters.download.ready"),
        state: "success",
        duration: 4,
      })
    );
    window.open(url);
  };

  useEffect(() => {
    if (!documentsFilter) return;

    const ensureArray = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") return value.split(",").filter((v) => v);
      return [];
    };

    const normalizedFilter = { ...documentsFilter };
    if (normalizedFilter.obligationTypes) {
      normalizedFilter.obligationTypes = ensureArray(normalizedFilter.obligationTypes);
    }
    if (normalizedFilter.status) {
      normalizedFilter.status = ensureArray(normalizedFilter.status);
    }
    if (normalizedFilter.documentCodes) {
      normalizedFilter.documentCodes = ensureArray(normalizedFilter.documentCodes);
    }

    setVariables((prevVariables) => {
      const hasChanges =
        Object.keys(normalizedFilter).some(
          (key) => normalizedFilter[key] !== prevVariables[key]
        ) ||
        Object.keys(prevVariables).some(
          (key) => !(key in normalizedFilter) && prevVariables[key] != null
        );
      return hasChanges ? { ...prevVariables, ...normalizedFilter } : prevVariables;
    });

    setSearchInputValues((prev) => ({
      ...prev,
      amountStart:
        normalizedFilter.amountStart != null
          ? String(normalizedFilter.amountStart)
          : "",
      amountEnd:
        normalizedFilter.amountEnd != null ? String(normalizedFilter.amountEnd) : "",
      rut: normalizedFilter.rut || "",
      name: normalizedFilter.name || "",
      docNum: normalizedFilter.docNum || "",
      documentCodes: normalizedFilter.documentCodes
        ? ensureArray(normalizedFilter.documentCodes)
        : [],
      obligationTypes: normalizedFilter.obligationTypes
        ? ensureArray(normalizedFilter.obligationTypes)
        : [],
      EERRAreas: normalizedFilter.EERRAreas
        ? ensureArray(normalizedFilter.EERRAreas)
        : [],
      cashflowAreaId: normalizedFilter.cashflowAreaId
        ? ensureArray(normalizedFilter.cashflowAreaId)
        : [],
      status: normalizedFilter.status ? ensureArray(normalizedFilter.status) : [],
    }));
  }, [documentsFilter]);

  useEffect(() => {
    dispatch(setUpdateDocumentsTypes("NEW"));
  }, []);

  // Click outside handler to close filter popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeFilter && filtersContainerRef.current && !filtersContainerRef.current.contains(event.target)) {
        // Check if click is inside a popup
        const popupElement = document.querySelector('.document-filter-popup-wrapper');
        if (popupElement && popupElement.contains(event.target)) {
          return;
        }
        setActiveFilter(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilter]);

  const [filterDocumentTypesSelected, setFilterDocumentTypesSelected] = useState([]);

  useEffect(() => {
    if (
      documentTypesState === "UPDATED" &&
      filterDocumentTypesSelected.length === 0
    ) {
      setFilterDocumentTypesSelected(documentTypes);
    }
    if (documentTypesState !== "UPDATED") {
      setFilterDocumentTypesSelected([]);
    }
  }, [documentTypes, documentTypesState]);

  const parseFilterInput = (route, value) => {
    if (["amountStart", "amountEnd"].includes(route)) {
      return parseFilterAmount(value);
    }
    if (route === "rut") {
      return value.replace(/[^0-9Kk]/g, "");
    }
    if (route === "hasInnerComments" || route === "isReported") {
      if (value === true || value === false) return value;
      if (value === "true") return true;
      if (value === "false") return false;
      return null;
    }
    return value;
  };

  const applyFilter = (filterName, value) => {
    const parseValue = parseFilterInput(filterName, value);
    const newVariables = { ...variables, [filterName]: parseValue };
    setVariables(newVariables);
    
    // Update URL
    const newSearchParams = new URLSearchParams(location.search);
    
    if (parseValue != null && parseValue !== '' && (!Array.isArray(parseValue) || parseValue.length > 0)) {
      if (Array.isArray(parseValue)) {
        newSearchParams.set(filterName, parseValue.join(","));
      } else if (typeof parseValue === 'boolean') {
        newSearchParams.set(filterName, parseValue.toString());
      } else if (filterName === 'dateStart' || filterName === 'dateEnd') {
        newSearchParams.set(filterName, moment.utc(parseValue).format('YYYY-MM-DD'));
      } else {
        newSearchParams.set(filterName, parseValue);
      }
    } else {
      newSearchParams.delete(filterName);
    }
    
    newSearchParams.set('page', '1');
    history.push({ search: newSearchParams.toString() });
    
    // Dispatch filter
    dispatch(importDocumentsFilter(newVariables));
    
    // Update enhanced icons
    const filteredVariables = Object.fromEntries(
      Object.entries(newVariables).filter(([key, value]) => value !== "" && value !== null && value !== undefined)
    );
    
    if (Object.keys(filteredVariables).length === 0) {
      dispatch(importEnhancedIcons({ documentostributarios: { icon: "" } }));
    } else {
      dispatch(importEnhancedIcons({ documentostributarios: { icon: "filterIcon" } }));
    }
    
    setActiveFilter(null);
  };

  const clearFilter = (filterName) => {
    const newVariables = { ...variables };
    delete newVariables[filterName];
    setVariables(newVariables);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete(filterName);
    newSearchParams.set('page', '1');
    history.push({ search: newSearchParams.toString() });
    
    dispatch(importDocumentsFilter(newVariables));
    
    const filteredVariables = Object.fromEntries(
      Object.entries(newVariables).filter(([key, value]) => value !== "" && value !== null && value !== undefined)
    );
    
    if (Object.keys(filteredVariables).length === 0) {
      dispatch(importEnhancedIcons({ documentostributarios: { icon: "" } }));
    } else {
      dispatch(importEnhancedIcons({ documentostributarios: { icon: "filterIcon" } }));
    }
  };

  const hasActiveFilter = (filterName) => {
    const value = variables[filterName];
    if (value == null || value === '') return false;
    if (typeof value === 'boolean') return true;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  // Handle date preset selection
  const handleDatePresetSelect = (preset, presetStartDate, presetEndDate) => {
    if (preset === 'RESET') {
      clearFilter('dateStart');
      clearFilter('dateEnd');
      return;
    }

    if (presetStartDate && presetEndDate) {
      // Apply both dates atomically to avoid race conditions
      // Parse as UTC to ensure the date doesn't shift when converting to ISO
      const dateStartISO = moment.utc(presetStartDate, 'YYYY-MM-DD').toISOString();
      const dateEndISO = moment.utc(presetEndDate, 'YYYY-MM-DD').toISOString();
      
      const newVariables = { ...variables, dateStart: dateStartISO, dateEnd: dateEndISO };
      setVariables(newVariables);
      
      // Update URL (YYYY-MM-DD only for readability)
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('dateStart', presetStartDate);
      newSearchParams.set('dateEnd', presetEndDate);
      newSearchParams.set('page', '1');
      history.push({ search: newSearchParams.toString() });
      
      // Dispatch filter
      dispatch(importDocumentsFilter(newVariables));
      
      // Update enhanced icons
      const filteredVariables = Object.fromEntries(
        Object.entries(newVariables).filter(([key, value]) => value !== "" && value !== null && value !== undefined)
      );
      
      if (Object.keys(filteredVariables).length === 0) {
        dispatch(importEnhancedIcons({ documentostributarios: { icon: null } }));
      } else {
        dispatch(importEnhancedIcons({ documentostributarios: { icon: "filterIcon" } }));
      }
    }
  };

  const documentCodeList = Object.keys(documentsDic)
    .filter((f) => documentsDic[parseInt(f)]?.isDocumentCodeInUse)
    // /documentos_tributarios should NOT offer Boleta de Honorarios (handled in its own module)
    .filter((f) => {
      if (location.pathname === "/documentos_tributarios") return String(f) !== "0";
      return true;
    });

  const statusOptions = useMemo(() => getDocumentStatusOptions(), [i18n.language]);
  const documentTypesOptions = useMemo(() => getDocumentTypeFilterOptions(), [i18n.language]);

  // Function to apply obligation type filter
  const applyObligationFilter = () => {
    // Ensure obligationTypes is an array
    let obligationTypesValue = searchInputValues.obligationTypes;
    if (!Array.isArray(obligationTypesValue)) {
      // If it's a string, convert to array
      if (obligationTypesValue && typeof obligationTypesValue === 'string') {
        obligationTypesValue = [obligationTypesValue];
      } else if (obligationTypesValue) {
        obligationTypesValue = [obligationTypesValue];
      } else {
        obligationTypesValue = [];
      }
    }
    applyFilter('obligationTypes', obligationTypesValue);
    setActiveFilter(null);
  };

  // Handler for Enter key press in obligation filter
  const handleObligationKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyObligationFilter();
    }
  };

  // Function to apply EERR filter
  const applyEERRFilter = () => {
    applyFilter('EERRAreas', searchInputValues.EERRAreas);
    setActiveFilter(null);
  };

  // Handler for Enter key press in EERR filter
  const handleEERRKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyEERRFilter();
    }
  };

  // Function to apply Cashflow filter
  const applyCashflowFilter = () => {
    applyFilter('cashflowAreaId', searchInputValues.cashflowAreaId);
    setActiveFilter(null);
  };

  // Handler for Enter key press in Cashflow filter
  const handleCashflowKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyCashflowFilter();
    }
  };

  const filterIcons = [];

  // Estado de pago
  filterIcons.push({
    id: 'status',
    icon: 'check-circle',
    label: t('filters.labels.status'),
    hasFilter: hasActiveFilter('status'),
    content: (
      <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
        <div
          onClick={() => {
            clearFilter('status');
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            !hasActiveFilter('status')
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.all')}
        </div>
        {statusOptions.map((statusOption) => {
          const isSelected = variables?.status?.includes(statusOption.value);
          return (
            <div
              key={statusOption.value}
              onClick={() => {
                const currentStatus = variables.status || [];
                const newStatus = isSelected
                  ? currentStatus.filter((s) => s !== statusOption.value)
                  : [...currentStatus, statusOption.value];

                if (newStatus.length === 0) {
                  clearFilter('status');
                } else {
                  applyFilter('status', newStatus);
                }
              }}
              className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
                isSelected
                  ? 'document-filter-option-selected'
                  : 'tw-text-gray-700'
              }`}
            >
              {statusOption.text}
            </div>
          );
        })}
      </div>
    ),
  });

  // Contabilidad
  if (canShowAccounting) {
    filterIcons.push({
      id: 'accounting',
      icon: 'book',
      label: t('filters.labels.accounting'),
      hasFilter: hasActiveFilter('accountingStatus'),
      content: (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
          <div
            onClick={() => {
              clearFilter('accountingStatus');
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              !hasActiveFilter('accountingStatus')
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.all')}
          </div>
          <div
            onClick={() => {
              const newValue =
                variables.accountingStatus === 'COMPLETED' ? null : 'COMPLETED';
              if (newValue) {
                applyFilter('accountingStatus', newValue);
              } else {
                clearFilter('accountingStatus');
              }
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              variables.accountingStatus === 'COMPLETED'
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.completed')}
          </div>
          <div
            onClick={() => {
              const newValue =
                variables.accountingStatus === 'IN_PROGRESS'
                  ? null
                  : 'IN_PROGRESS';
              if (newValue) {
                applyFilter('accountingStatus', newValue);
              } else {
                clearFilter('accountingStatus');
              }
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              variables.accountingStatus === 'IN_PROGRESS'
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.inProgress')}
          </div>
          <div
            onClick={() => {
              const newValue =
                variables.accountingStatus === 'NOT_STARTED'
                  ? null
                  : 'NOT_STARTED';
              if (newValue) {
                applyFilter('accountingStatus', newValue);
              } else {
                clearFilter('accountingStatus');
              }
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              variables.accountingStatus === 'NOT_STARTED'
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.notStarted')}
          </div>
          <div
            onClick={() => {
              const newValue =
                variables.accountingStatus === 'EMISSION' ? null : 'EMISSION';
              if (newValue) {
                applyFilter('accountingStatus', newValue);
              } else {
                clearFilter('accountingStatus');
              }
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              variables.accountingStatus === 'EMISSION'
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.emission')}
          </div>
          <div
            onClick={() => {
              const newValue =
                variables.accountingStatus === 'MATCH' ? null : 'MATCH';
              if (newValue) {
                applyFilter('accountingStatus', newValue);
              } else {
                clearFilter('accountingStatus');
              }
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              variables.accountingStatus === 'MATCH'
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.match')}
          </div>
        </div>
      ),
    });
  }

  // Comentarios
  filterIcons.push({
    id: 'comments',
    label: 'Comentarios',
    hasFilter: hasActiveFilter('hasInnerComments'),
    content: (
      <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
        <div
          onClick={() => {
            clearFilter('hasInnerComments');
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            !hasActiveFilter('hasInnerComments')
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.all')}
        </div>
        <div
          onClick={() => {
            applyFilter('hasInnerComments', true);
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            variables.hasInnerComments === true
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.comments.with')}
        </div>
        <div
          onClick={() => {
            applyFilter('hasInnerComments', false);
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            variables.hasInnerComments === false
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.comments.without')}
        </div>
      </div>
    ),
  });

  // Reportados
  filterIcons.push({
    id: 'reported',
    icon: 'flag',
    label: t('filters.reported'),
    hasFilter: hasActiveFilter('isReported'),
    content: (
      <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
        <div
          onClick={() => {
            clearFilter('isReported');
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            !hasActiveFilter('isReported')
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.all')}
        </div>
        <div
          onClick={() => {
            applyFilter('isReported', true);
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            variables.isReported === true
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.reported')}
        </div>
        <div
          onClick={() => {
            applyFilter('isReported', false);
          }}
          className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
            variables.isReported === false
              ? 'document-filter-option-selected'
              : 'tw-text-gray-700'
          }`}
        >
          {t('filters.notReported')}
        </div>
      </div>
    ),
  });

  // Boletas de honorarios: separar boletas de retenciones (filtro `bhView`,
  // resuelto en backend igual que el resto de los filtros).
  if (location.pathname === "/boletas") {
    filterIcons.push({
      id: 'bhView',
      icon: 'file-text',
      label: t('filters.labels.boletaView'),
      hasFilter: hasActiveFilter('bhView'),
      content: (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
          <div
            onClick={() => {
              clearFilter('bhView');
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              !hasActiveFilter('bhView')
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.all')}
          </div>
          {[
            ['BOLETAS', t('filters.boletaView.boletas')],
            ['RETENCIONES', t('filters.boletaView.retenciones')],
          ].map(([value, label]) => (
            <div
              key={value}
              onClick={() => {
                applyFilter('bhView', value);
              }}
              className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
                variables.bhView === value
                  ? 'document-filter-option-selected'
                  : 'tw-text-gray-700'
              }`}
            >
              {label}
            </div>
          ))}
        </div>
      ),
    });
  }

  // Add documentCode filter only if NOT in /boletas, /obligaciones, /cuotas_de_creditos, or DIN routes
  // (DIN is its own document kind; showing DTE codes there is confusing)
  // (cuotas_de_creditos only shows CREDIT documents, so document code filter is not applicable)
  if (!["/boletas", "/obligaciones", "/cuotas_de_creditos", "/declaraciones-ingreso", "/declaraciones_de_ingresos"].includes(location.pathname)) {
    filterIcons.push({
      id: 'documentCode',
      icon: 'file-text',
      label: t('filters.labels.documentType'),
      hasFilter: hasActiveFilter('documentCodes'),
      content: (
        <div className="tw-flex tw-flex-col tw-gap-1 tw-max-h-60 tw-overflow-y-auto document-filter-options">
          <div
            onClick={() => {
              clearFilter('documentCodes');
            }}
            className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
              !hasActiveFilter('documentCodes')
                ? 'document-filter-option-selected'
                : 'tw-text-gray-700'
            }`}
          >
            {t('filters.accounting.all')}
          </div>
          {documentCodeList.map((docCode) => {
            const isSelected = variables?.documentCodes?.includes(docCode);
            return (
              <div
                key={docCode}
                onClick={() => {
                  const currentCodes = variables.documentCodes || [];
                  const newCodes = isSelected
                    ? currentCodes.filter(c => c !== docCode)
                    : [...currentCodes, docCode];
                  
                  if (newCodes.length === 0) {
                    clearFilter('documentCodes');
                  } else {
                    applyFilter('documentCodes', newCodes);
                  }
                }}
                className={`tw-px-3 tw-py-2 tw-cursor-pointer tw-transition-colors tw-border tw-border-gray-300 tw-rounded-md document-filter-option ${
                  isSelected
                    ? 'document-filter-option-selected'
                    : 'tw-text-gray-700'
                }`}
              >
                {translateDocumentFullName(parseInt(docCode))}
              </div>
            );
          })}
        </div>
      ),
    });
  }

  // Add obligation filter if we're in obligaciones route (después de contabilidad + estado de pago)
  if (location.pathname === "/obligaciones") {
    const obligationInsertIndex = filterIcons.findIndex((f) => f.id === 'reported') + 1;
    filterIcons.splice(obligationInsertIndex, 0, {
      id: 'obligationType',
      icon: 'clipboard',
      label: t('filters.labels.obligationType'),
      hasFilter: hasActiveFilter('obligationTypes'),
      content: (
        <div className="document-filter-popup" style={{ minWidth: '350px' }}>
          <div className="tw-p-3">
            <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2 tw-block">
              {t('filters.obligationTypeUpper')}
            </label>
            <div onKeyDown={handleObligationKeyPress}>
              <ObligationsDropdown
                includeRendiciones
                onChange={(_, v) => {
                  // Convert single value to array for obligationTypes filter
                  const value = v.value;
                  const arrayValue = value ? (Array.isArray(value) ? value : [value]) : [];
                  setSearchInputValues({ ...searchInputValues, obligationTypes: arrayValue });
                }}
                value={
                  Array.isArray(searchInputValues.obligationTypes) && searchInputValues.obligationTypes.length > 0
                    ? searchInputValues.obligationTypes[0]
                    : Array.isArray(variables.obligationTypes) && variables.obligationTypes.length > 0
                    ? variables.obligationTypes[0]
                    : undefined
                }
                placeholder={t('filters.obligationTypePlaceholder')}
              />
            </div>
            <div className="tw-flex tw-gap-2 tw-mt-3 tw-justify-between">
              <Button
                size="small"
                onClick={() => {
                  clearFilter('obligationTypes');
                  setSearchInputValues({ ...searchInputValues, obligationTypes: [] });
                  setActiveFilter(null);
                }}
              >
                {t('common.clear')}
              </Button>
              <Button
                size="small"
                color="blue"
                onClick={applyObligationFilter}
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </div>
      ),
    });
  }

  // Add EERR and Cashflow filters if plan allows
  if (SHOW_AREA_FILTERS && !plan.includes(Plans.estado_de_resultados)) {
    filterIcons.push({
      id: 'eerrArea',
      icon: 'pie-chart',
      label: t('filters.labels.eerrArea'),
      hasFilter: hasActiveFilter('EERRAreas'),
      content: (
        <div className="document-filter-popup" style={{ minWidth: '280px' }}>
          <div className="tw-p-3">
            <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2 tw-block">
              {t('filters.eerrAreaUpper')}
            </label>
            <div onKeyDown={handleEERRKeyPress}>
              <SelectorEERR
                value={searchInputValues.EERRAreas || variables.EERRAreas}
                name="EERRAreas"
                onChange={(_, v) => {
                  setSearchInputValues({ ...searchInputValues, EERRAreas: v.value });
                }}
                placeholder={t('filters.areasHint')}
                companyId={companyId}
                multiple={true}
                controlled={true}
                showWithoutArea={true}
                showIsHidden={true}
              />
            </div>
            <div className="tw-flex tw-gap-2 tw-mt-3 tw-justify-between">
              <Button
                size="small"
                onClick={() => {
                  clearFilter('EERRAreas');
                  setSearchInputValues({ ...searchInputValues, EERRAreas: [] });
                  setActiveFilter(null);
                }}
              >
                {t('common.clear')}
              </Button>
              <Button
                size="small"
                color="blue"
                onClick={applyEERRFilter}
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </div>
      ),
    });
  }

  if (SHOW_AREA_FILTERS && !plan.includes(Plans.smartcfo)) {
    filterIcons.push({
      id: 'cashflowArea',
      icon: 'trending-up',
      label: t('filters.labels.cashflowArea'),
      hasFilter: hasActiveFilter('cashflowAreaId'),
      content: (
        <div className="document-filter-popup" style={{ minWidth: '280px' }}>
          <div className="tw-p-3">
            <label className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2 tw-block">
              {t('filters.cashflowAreaUpper')}
            </label>
            <div onKeyDown={handleCashflowKeyPress}>
              <SelectorCashflow
                value={searchInputValues.cashflowAreaId || variables.cashflowAreaId}
                name="cashflowAreaId"
                onChange={(_, v) => {
                  setSearchInputValues({ ...searchInputValues, cashflowAreaId: v.value });
                }}
                placeholder={t('filters.areasHint')}
                companyId={companyId}
                multiple={true}
                controlled={true}
                showWithoutArea={true}
              />
            </div>
            <div className="tw-flex tw-gap-2 tw-mt-3 tw-justify-between">
              <Button
                size="small"
                onClick={() => {
                  clearFilter('cashflowAreaId');
                  setSearchInputValues({ ...searchInputValues, cashflowAreaId: [] });
                  setActiveFilter(null);
                }}
              >
                {t('common.clear')}
              </Button>
              <Button
                size="small"
                color="blue"
                onClick={applyCashflowFilter}
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </div>
      ),
    });
  }


  return (
    <div className="document-filters-container" ref={filtersContainerRef}>
      <div className="document-filters-icons">
        {filterIcons.map((filter) => (
          <Popup
            key={filter.id}
            trigger={
              <div
                className={`document-filter-icon ${filter.hasFilter ? 'active' : ''} ${activeFilter === filter.id ? 'hovered' : ''}`}
                onMouseEnter={() => setActiveFilter(filter.id)}
              >
                {filter.id === 'comments' ? (
                  <div className={`tw-flex tw-items-center tw-justify-center ${filter.hasFilter ? 'tw-text-[#488E9D]' : 'tw-text-gray-600'}`} style={{ width: '16px', height: '16px' }}>
                    <CommentFilterIcon size={16} strokeWidth={1.75} />
                  </div>
                ) : (
                <FeatherIcon
                  icon={filter.icon}
                  size={16}
                  className={filter.hasFilter ? 'tw-text-[#488E9D]' : 'tw-text-gray-600'}
                />
                )}
              </div>
            }
            content={filter.content}
            position="bottom center"
            open={activeFilter === filter.id}
            onClose={() => {}}
            className="document-filter-popup-wrapper"
            style={{ zIndex: 1000 }}
          />
        ))}
      </div>

      <DownloadFilteredButton
        onClick={handleDownloadExcel}
        loading={downloadFetching}
        label={t("filters.labels.download")}
        iconClassName="document-filter-icon"
      />
      
      <div className="document-filters-date-inputs-wrapper" style={{ overflow: 'visible' }}>
        <div className="document-filters-date-inputs" style={{ overflow: 'visible' }}>
          <DateRangeFilter
            startDate={variables.dateStart ? moment.utc(variables.dateStart).format('YYYY-MM-DD') : null}
            endDate={variables.dateEnd ? moment.utc(variables.dateEnd).format('YYYY-MM-DD') : null}
            onStartDateChange={(date) => {
              if (date) {
                // Convert YYYY-MM-DD to ISO string using UTC to avoid timezone shifts
                // Parse as UTC to ensure the date doesn't shift when converting to ISO
                const isoDate = moment.utc(date, 'YYYY-MM-DD').toISOString();
                applyFilter('dateStart', isoDate);
              } else {
                clearFilter('dateStart');
              }
            }}
            onEndDateChange={(date) => {
              if (date) {
                // Convert YYYY-MM-DD to ISO string using UTC to avoid timezone shifts
                // Parse as UTC to ensure the date doesn't shift when converting to ISO
                const isoDate = moment.utc(date, 'YYYY-MM-DD').toISOString();
                applyFilter('dateEnd', isoDate);
              } else {
                clearFilter('dateEnd');
              }
            }}
            onPresetSelect={handleDatePresetSelect}
            showActiveIndicator={hasActiveFilter('dateStart') || hasActiveFilter('dateEnd')}
            className="document-filters-date-range"
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentFilters;
